import React from 'react';
import {Roles} from 'app/Constants';
import {LookupProps} from 'components/grid-dx/MasterGrid';
import IntegrityRoleGrid from 'components/UserRole';
import {Column, Lookup} from 'devextreme-react/data-grid';
import {useLoadLookupCourse} from 'features/course/hooks';
import {i18n2} from 'features/i18n/i18n';
import {
  lookupCapa,
  lookupModality,
  lookupStatus,
  Status,
} from 'features/instance/component/StageGrid';
import useLoadLookupSectors from 'features/sectors/hooks/useLoadLookupSectors';
import {IntegrityInstanceGrid} from '../../../instance/component';
import {
  useAddStage,
  useDeleteStage,
  useLoadStage,
  useUpdateStage,
} from '../../../instance/hooks';
import {StageSchema} from '../../../instance/model/Stage';
import Buttons, {MAX_ACTION_BUTTONS} from '../buttons/Buttons';
import {useUtilInformation} from 'components/hooks';
import {getActionColumnWidth} from 'components/grid-dx/styles/actionButtonStyles';
import {useIsTablet} from 'components/hooks/useIsTablet';

const StageManagement = () => {
  const {token, cwid, role} = useUtilInformation();
  const isTablet = useIsTablet();

  const loadLookupSectors = useLoadLookupSectors({token, cwid, role});
  const loadLookupCourse = useLoadLookupCourse();
  const loadStage = useLoadStage(false);
  const addStage = useAddStage();
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStage();

  return (
    <>
      <IntegrityRoleGrid
        allowEdit={[
          Roles.AdminQA,
          Roles.AdminHSE,
          Roles.AdminITPS,
          Roles.Trainer,
        ]}
        schema={StageSchema(true)}
        initialValues={{duration: 1}}
        id={['iD_Instance', 'iD_Course']}
        dataSource={loadStage}
        promiseAdd={addStage}
        promiseEdit={updateStage}
        promiseDelete={deleteStage}
        withoutEdit={{column: 'status', equals: [1, 2, 4]}}
        remoteLookup={[
          {
            column: 'iD_Course',
            keyValue: 'iD_Course',
            displayValue: 'name',
            dataSource: loadLookupCourse,
          },
          {
            column: 'sector',
            keyValue: 'iD_Sector',
            displayValue: 'name',
            dataSource: loadLookupSectors,
          },
        ]}
        withoutDelete={{column: 'status', equals: [1]}}
        columnOptions={columnOptions}
        integrityGrid={IntegrityInstanceGrid}
        rowIntegrityGrid={['ID_Instance', 'ID_Course']}
        integrityId={['ID_Instance', 'ID_Course']}
        integrityName={'Instance'}
        exportName="Instance management-export">
        <Column caption={i18n2._('#Course')} dataField="iD_Course" width="4%" />
        <Column
          caption={i18n2._('#Instancia')}
          dataField="iD_Instance"
          width="4%"
        />
        <Column
          caption={i18n2._('Nombre del curso')}
          dataField="course_Name"
          width="30%"
        />
        <Column
          caption={i18n2._('Documento')}
          dataField="doc_Number"
          width="10%"
        />
        <Column
          caption={i18n2._('Modalidad')}
          dataField="modality"
          visible={false}>
          <Lookup
            dataSource={lookupModality}
            valueExpr="id"
            displayExpr={(item: LookupProps) => {
              return i18n2._(item ? item.name : '');
            }}
          />
        </Column>
        <Column
          caption={i18n2._('#Sector')}
          dataField="sector"
          visible={false}
        />
        <Column
          caption={i18n2._('Fecha de actualizacion')}
          visible={true}
          dataField="updateDate"
          defaultSortOrder={'desc'}
          dataType="date"
          format="dd/MM/yyyy"
          calculateCellValue={(row: any) => new Date(row.updateDate)}
          width="6%"
        />
        <Column
          caption={i18n2._('Vencimiento de la instancia')}
          dataField="expiration_Date"
          dataType="date"
          format="dd/MM/yyyy"
          calculateCellValue={(row: any) =>
            row.expiration_Date ? new Date(row.expiration_Date) : null
          }
          width="6%"
        />

        <Column
          caption={i18n2._('Fecha de instancia')}
          dataField="instance_Date"
          dataType="date"
          format="dd/MM/yyyy"
          calculateCellValue={(row: any) => new Date(row.instance_Date)}
          visible={false}
        />
        <Column
          caption={i18n2._('Estado')}
          dataField="status"
          width="%5"
          cellComponent={(props) => (
            <Status status={props.data.value} text={props.data.text} />
          )}>
          <Lookup
            dataSource={lookupStatus}
            valueExpr="id"
            displayExpr={(item: LookupProps) => {
              return i18n2._(item ? item.name : '');
            }}
          />
        </Column>
        <Column
          caption={i18n2._('Observacion')}
          dataField="observations"
          visible={false}
        />
        <Column
          caption={i18n2._('Capa inicial')}
          dataField="capaIniLeg"
          width="7%">
          <Lookup
            dataSource={lookupCapa}
            valueExpr="id"
            displayExpr={(item: LookupProps) => {
              return i18n2._(item ? item.name : '');
            }}
          />
        </Column>

        <Column
          caption={i18n2._('Duración (hs)')}
          visible={false}
          dataField="duration"
        />
        <Column
          caption={i18n2._('Instructor')}
          dataField="instructor"
          visible={false}
        />
        <Column
          caption={i18n2._('Actualizado por')}
          dataField="updatedBy"
          width="7%"
        />
        <Column
          caption={i18n2._('Nombre del sector')}
          dataField="sector_Name"
          width="9%"
          alignment="left"
        />
        <Column
          caption={i18n2._('Revision')}
          dataField="revision"
          visible={false}
        />
        <Column caption={i18n2._('Motivo')} dataField="reason" width="17%" />

        <Column
          caption={i18n2._('Eliminado')}
          dataField="deleted"
          width={100}
          visible={false}
        />
        <Column
          caption=""
          cellComponent={(e: any) => (
            <Buttons data={e.data.data} id={e.data.key} />
          )}
          width={getActionColumnWidth(MAX_ACTION_BUTTONS, isTablet)}
          fixed
          alignment="right"
          visible={true}
        />
      </IntegrityRoleGrid>
    </>
  );
};

const columnOptions = [
  {
    column: 'iD_Instance',
    allowEdit: false,
    hiddenAdd: true,
  },
  {
    selectType: 'multipleName',
    column: 'iD_Course',
    lookupColumn: 'version',
    myLabel: i18n2._('Curso'),
    allowEdit: false,
  },
  {
    column: 'course_Name',
    hiddenEdit: true,
    hiddenAdd: true,
  },
  {
    column: 'doc_Number',
    hiddenEdit: true,
    hiddenAdd: true,
  },
  {
    column: 'sector_Name',
    hiddenEdit: true,
    hiddenAdd: true,
  },
  {
    column: 'sector',
    myLabel: i18n2._('Sector'),
    allowEdit: true,
  },
  {
    column: 'status',
    hiddenAdd: true,
    hiddenEdit: true,
  },
  {
    column: 'instructor',
    sizeMobile: 12,
    sizeDesktop: 6,
    required: true,
  },
  {
    column: 'duration',
    myLabel: i18n2._('Duración (hs)'),
    required: true,
  },
  {
    selectType: 'enum',
    column: 'modality',
    allowEdit: false,
    data: [
      {
        key: 0,
        value: 'Online',
      },
      {
        key: 1,
        value: 'Tablet',
      },
      {
        key: 2,
        value: i18n2._('InPerson'),
      },
    ],
  },
  {
    column: 'capaIniLeg',
    hiddenEdit: true,
    hiddenAdd: true,
  },
  {
    selectType: 'dateByCourse',
    column: 'expiration_Date',
    required: true,
  },
];

export default StageManagement;
